import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ScanTicketInstructionsIcon } from "assets/images/scan-passport-instructions.svg";
import { Box, Text } from "components";
import useCamera from "hooks/useCamera";
import useMRZ from "hooks/useMRZ";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getTranslationsStyle } from "utils/arabisation.util";

import FixedOverlay from "../../components/FixedOverlay";

const FullScreenCamera = styled.div`
  top: 0;
  height: 100svh;
  width: 100%;
  position: fixed;
  overflow: hidden;
  z-index: ${(props) => props.theme.zIndex.modalClose};
  background-color: ${(props) => props.theme.colors.backgroundQuaternary};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

function OCRScanner({
  onScanComplete,
  onClose,
  pauseScanner,
  maxDetectionTime = 2000,
  scanAnalytics,
}) {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const timeoutRef = useRef(null);
  const [canScan, setCanScan] = useState(true);

  useCamera(videoRef);
  const { scanStatus } = useMRZ(onScanComplete, pauseScanner, scanAnalytics);

  const captureAndSendFrame = () => {
    if (
      pauseScanner.current ||
      !videoRef.current ||
      !canvasRef.current ||
      !canScan
    ) {
      return;
    }

    const video = videoRef.current;

    // Make sure video is ready
    if (!video.videoWidth || !video.videoHeight) {
      setTimeout(captureAndSendFrame, 500);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageBlob = canvas.toDataURL("image/jpeg");

    // Skip empty frames
    if (imageBlob === "data:," || imageBlob === "data:image/jpeg;base64,") {
      setTimeout(captureAndSendFrame, 500);
      return;
    }

    console.log("Sending frame for OCR processing");

    // Mark the time when a frame is sent for OCR processing
    if (scanAnalytics && scanAnalytics.markFrameSent) {
      scanAnalytics.markFrameSent();
    }

    // Set canScan to false to prevent sending more frames until processing completes
    setCanScan(false);

    const postData = {
      action: "SCAN_IMAGE",
      payload: { url: imageBlob },
    };

    window.postMessage(postData, window.location.origin);
  };

  // Effect for handling the timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [maxDetectionTime]);

  // Effect to handle processing complete notifications
  useEffect(() => {
    const handleProcessingComplete = (event) => {
      if (event.data?.action === "READY_FOR_NEXT_SCAN") {
        // When processing is complete, we can scan again
        setCanScan(true);
      }
    };

    window.addEventListener("message", handleProcessingComplete);

    return () => {
      window.removeEventListener("message", handleProcessingComplete);
    };
  }, []);

  // Effect to start scanning and handle scan status
  useEffect(() => {
    if (scanStatus === "success") {
      // Clear timeout if scan is successful
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Start a scan whenever canScan becomes true and we're not paused
    if (canScan && !pauseScanner.current) {
      setTimeout(captureAndSendFrame, 200);
    }
  }, [canScan, scanStatus]);

  // Initial scan
  useEffect(() => {
    // Wait a bit for camera to initialize before first scan
    const initialTimer = setTimeout(() => {
      if (canScan && !pauseScanner.current) {
        captureAndSendFrame();
      }
    }, 1000);

    return () => clearTimeout(initialTimer);
  }, []);

  useEffect(() => {
    if (scanAnalytics && scanAnalytics.markCameraOpened) {
      scanAnalytics.markCameraOpened();
    }
  }, [scanAnalytics]);

  return (
    <FullScreenCamera>
      <VideoContainer>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        <Box
          position="absolute"
          bottom="6vw"
          left="50%"
          w="65vw"
          d="flex"
          direction="column"
          gap="xs"
          justifyContent="center"
          alignItems="center"
          style={{ transform: "translateX(-50%)", zIndex: 1 }}
        >
          <ScanTicketInstructionsIcon />
          <Text
            color="textQuaternary"
            fontSize="l"
            textAlign="center"
            style={getTranslationsStyle(i18n, { textAlign: "center" })}
          >
            {t("smart_gate.scanning_instructions")}
          </Text>
        </Box>
        <FixedOverlay
          isScanUnsuccessful={scanStatus === "partial"}
          isScanSuccessful={scanStatus === "success"}
          onClose={onClose}
        />
      </VideoContainer>
    </FullScreenCamera>
  );
}

export default OCRScanner;
